import React from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { LoadingSpinner } from '../../components/common/LoadingSpinner';
import { Modal } from '../../components/common/Modal';
import { useToast } from '../../components/common/Toast';
import { useWeightUnit } from '../../hooks/useWeightUnit';
import type { CreateWorkOrderData, WorkOrder } from '../../types/workOrder';
import type { MachineFormData } from '../../types/machine';
import type { RoasterFormData } from '../../types/roaster';
import { machineService } from '../../services/machineService';
import { roasterService } from '../../services/roasterService';
import { workOrderService } from '../../services/workOrderService';
import { BasicInfoSection } from './components/BasicInfoSection';
import { BeanSelectionSection } from './components/BeanSelectionSection';
import { RoastProfileSectionWrapper } from './components/RoastProfileSectionWrapper';
import { WorkOrderActionsBar } from './components/WorkOrderActionsBar';
import { WorkOrderModals } from './components/WorkOrderModals';
import { CompleteWorkOrderSummaryModal } from './components/CompleteWorkOrderSummaryModal';
import { RoastTypeWarningModal } from './components/RoastTypeWarningModal';
import { UnlinkArtisanProfileModal } from './components/UnlinkArtisanProfileModal';
import { invalidateArtisanLinkQueries } from '../../lib/artisanLinkQueries';
import { useWorkOrderForm } from './hooks/useWorkOrderForm';
import { MachineForm } from '../production/MachineForm';
import { RoasterForm } from '../production/RoasterForm';
import { useLanguage } from '../../features/language/useLanguage';
import { usePermissions } from '../../hooks/usePermissions';
import { useSubscription } from '../../hooks/useSubscription';

interface CreateWorkOrderFormProps {
  onSubmit: (data: CreateWorkOrderData, action: 'draft' | 'schedule' | 'complete') => void;
  onCancel: () => void;
  initialData?: Partial<CreateWorkOrderData> | Partial<WorkOrder>;
  readOnly?: boolean;
}

export const CreateWorkOrderForm: React.FC<CreateWorkOrderFormProps> = ({ onSubmit, onCancel, initialData, readOnly = false }) => {
  const { t } = useLanguage();
  const { unit, setUnit } = useWeightUnit();
  const { showToast } = useToast();
  const queryClient = useQueryClient();
  const { can } = usePermissions();
  // Hide the "+ Create…" autocomplete option when the user only has read on
  // the underlying resource — otherwise clicking it 403s on POST /machines or
  // /roasters mid-WO-flow.
  const canCreateMachine = can('machines', 'write');
  const canCreateRoaster = can('roasters', 'write');
  const { tier: subTier, machineTypeQuota } = useSubscription();
  const isAtMachineTypeLimit =
    subTier !== 'plus' &&
    machineTypeQuota !== null &&
    machineTypeQuota.limit >= 0 &&
    machineTypeQuota.used >= machineTypeQuota.limit;
  const [isMachineModalOpen, setIsMachineModalOpen] = React.useState(false);
  const [isRoasterModalOpen, setIsRoasterModalOpen] = React.useState(false);
  const [isUnlinkModalOpen, setIsUnlinkModalOpen] = React.useState(false);
  const [isUnlinkingArtisanProfile, setIsUnlinkingArtisanProfile] = React.useState(false);
  const [machineDraftName, setMachineDraftName] = React.useState('');
  const [roasterDraftName, setRoasterDraftName] = React.useState('');
  const [isRoastTypeWarningOpen, setIsRoastTypeWarningOpen] = React.useState(false);
  const [isSavingRoastType, setIsSavingRoastType] = React.useState(false);
  const [roastTypeSaveError, setRoastTypeSaveError] = React.useState<string | null>(null);
  const {
    register,
    control,
    setValue,
    handleSubmit,
    errors,
    isSubmitting,
    isLoading,
    recipes,
    greenBeans,
    machines,
    roasters,
    setMachines,
    setRoasters,
    manualInputWeight,
    manualOutputWeight,
    isAutoCalculatingLots,
    selectedBeanId,
    lotOptions,
    lots,
    useRoastProfile,
    setUseRoastProfile,
    selectedRecipeId,
    setSelectedRecipeId,
    useSnapshot,
    setUseSnapshot,
    saveAsNewRecipe,
    setSaveAsNewRecipe,
    useStopwatchMode,
    setUseStopwatchMode,
    artisanProfiles,
    selectedArtisanProfileId,
    linkedArtisanProfileId,
    linkedArtisanProfileTitle,
    handleArtisanProfileSelect,
    handleArtisanProfileUnlinked,
    useArtisanProfile,
    setUseArtisanProfile,
    roastData,
    roastType,
    setRoastType,
    isEditMode,
    initialStatus,
    submitActionRef,
    stage,
    seconds,
    stopwatchRef,
    saving,
    saved,
    canAutosave,
    handleProfileChange,
    handleStageChange,
    handleWeightInputChange,
    handleWeightOutputChange,
    handleAgtronChange,
    handleRoastTypeChange,
    handleStopwatchMainClick,
    handleStopwatchStart,
    handleStopwatchPause,
    handleStopwatchRestart,
    handleAutoCalculateLots,
    handleLotChange,
    handleWeightChange,
    addLotRow,
    removeLotRow,
    handleBeanChange,
    totalUsedWeight,
    parsedRoastInputWeight,
    isWeightMismatch,
    isOutputExceedsInput,
    canSaveAsComplete,
    onboardingActive,
    onboardingRoastStep,
    pendingSubmitData,
    setPendingSubmitData,
    isMismatchModalOpen,
    setIsMismatchModalOpen,
    isOutputGreaterModalOpen,
    setIsOutputGreaterModalOpen,
    isCompleteSummaryModalOpen,
    confirmCompleteSubmit,
    cancelCompleteSummary,
    isRoastTypeDirty,
    initialRoastType,
    commitRoastTypeAsInitial,
    warningMessage,
    setWarningMessage,
    onFormSubmit,
    selectedMachine,
  } = useWorkOrderForm({ initialData, onSubmit });

  const initialIsSavedAsRecipe =
    Boolean(initialData && 'isSavedAsRecipe' in initialData && initialData.isSavedAsRecipe) ||
    Boolean(initialData && 'is_saved_as_recipe' in initialData && (initialData as Record<string, unknown>).is_saved_as_recipe);
  // Block "Save as Scheduled" / "Save as Complete" while the stopwatch is in
  // the middle of a run — the user must either finish the roast (stage:
  // 'ended') or not have started it yet (stage: 'idle') before persisting.
  const isStopwatchInProgress =
    useRoastProfile === true &&
    useStopwatchMode === true &&
    stage !== 'idle' &&
    stage !== 'ended';
  const isReadOnly = initialStatus === 'completed' || initialStatus === 'cancelled' || readOnly;
  const allowRoastTypeEdit = initialStatus === 'completed' && !readOnly;
  const allowSaveRecipe = initialStatus === 'completed' && !initialIsSavedAsRecipe;
  const shouldPreventSubmit = initialStatus === 'cancelled' || (initialStatus === 'completed' && !allowSaveRecipe);
  const canDownload = initialStatus === 'completed' && Boolean(initialData && 'id' in initialData) && useRoastProfile === true;
  const canUnlinkArtisanProfile =
    Boolean(linkedArtisanProfileId) &&
    Boolean(initialData && 'id' in initialData && (initialData as WorkOrder).id) &&
    !isReadOnly;

  const downloadData = React.useMemo(() => {
    if (!canDownload || !initialData) return null;
    const wo = initialData as WorkOrder;
    const rawType = roastType || wo.roastType || '';
    const capitalizedType = rawType ? rawType.charAt(0).toUpperCase() + rawType.slice(1) : '';
    const finalRoastName = `${capitalizedType} ${wo.itemName || ''} ${wo.workOrderNumber || ''}`.trim();
    return {
      recipeName: finalRoastName,
      roastName: finalRoastName,
      machineName: wo.machine || '-',
      beanName: greenBeans.find(b => b.id === selectedBeanId)?.name || wo.itemName || '-',
      date: typeof wo.targetDate === 'string' ? wo.targetDate.split('T')[0] : (typeof wo.date === 'string' ? wo.date : new Date().toISOString().split('T')[0]),
      time: wo.time || '',
      roastData: roastData,
      roastType: capitalizedType,
      notes: wo.internalNotes,
      actualEnd: wo.endDate
        ? new Date(wo.endDate).toLocaleDateString('id-ID')
        : undefined,
      roaster: roasters.find(r => r.id === wo.roasterId)?.name || wo.roaster,
      lotName: lots.map(l => {
          const opt = lotOptions.find(o => o.id === l.lotId);
          const name = opt ? opt.name.split(' (')[0] : '';
          return name ? `• ${name} - ${l.usedWeight || 0}${t('inventory.history.unitKg')}` : '';
      }).filter(Boolean).join('\n'),
    };
  }, [canDownload, initialData, greenBeans, selectedBeanId, roastData, roastType, lots, lotOptions, roasters, t]);


  const roastTypeLabelMap = React.useMemo<Record<string, string>>(() => ({
    espresso: t('workOrders.beanSelection.roast.espresso'),
    omni: t('workOrders.beanSelection.roast.omni'),
    filter: t('workOrders.beanSelection.roast.filter'),
    sample: t('workOrders.beanSelection.roast.sample'),
  }), [t]);

  const completeSummaryData = React.useMemo(() => {
    const bean = greenBeans.find((b) => b.id === selectedBeanId);
    const summaryLots = lots
      .filter((row) => row.lotId && Number(row.usedWeight || 0) > 0)
      .map((row) => {
        const option = lotOptions.find((o) => o.id === row.lotId);
        const displayName = option?.name ? option.name.split(' (')[0] : row.lotId;
        return {
          lotId: row.lotId,
          lotName: displayName,
          usedWeight: Number(row.usedWeight || 0),
        };
      });
    const inputWeight = useRoastProfile
      ? Number(roastData.weightInput || 0)
      : Number(manualInputWeight || 0);
    const outputWeight = useRoastProfile
      ? Number(roastData.weightOutput || 0)
      : Number(manualOutputWeight || 0);
    const roastTypeKey = (roastType || '').toLowerCase();
    const roastTypeLabel = roastTypeKey ? roastTypeLabelMap[roastTypeKey] || roastTypeKey : undefined;
    return {
      beanName: bean?.name,
      lots: summaryLots,
      inputWeight,
      outputWeight,
      agtronWhole: roastData.agtron.wholeBean ? String(roastData.agtron.wholeBean) : undefined,
      agtronGround: roastData.agtron.groundBean ? String(roastData.agtron.groundBean) : undefined,
      agtronScaa: roastData.agtron.classification?.scaa || undefined,
      agtronCommon: roastData.agtron.classification?.common || undefined,
      roastTypeLabel,
    };
  }, [
    greenBeans,
    selectedBeanId,
    lots,
    lotOptions,
    useRoastProfile,
    roastData.weightInput,
    roastData.weightOutput,
    roastData.agtron,
    manualInputWeight,
    manualOutputWeight,
    roastType,
    roastTypeLabelMap,
  ]);

  const machineOptions = React.useMemo(
    () =>
      machines
        .slice()
        .filter((machine) => machine.isActive)
        .sort((a, b) => a.name.localeCompare(b.name))
        .map((machine) => ({
          id: machine.id,
          label: machine.name,
          text: machine.name,
          value: machine.id,
        })),
    [machines]
  );

  const roasterOptions = React.useMemo(
    () =>
      roasters
        .slice()
        .filter((roaster) => roaster.isActive)
        .sort((a, b) => a.name.localeCompare(b.name))
        .map((roaster) => ({
          id: roaster.id,
          label: roaster.name,
          text: roaster.name,
        })),
    [roasters]
  );

  const handleCreateMachine = (text: string) => {
    setMachineDraftName(text);
    setIsMachineModalOpen(true);
  };

  const handleCreateRoaster = (text: string) => {
    setRoasterDraftName(text);
    setIsRoasterModalOpen(true);
  };

  const handleSaveMachine = async (data: MachineFormData) => {
    try {
      const created = await machineService.create(data);
      setMachines((prev) => [...prev, created].sort((a, b) => a.name.localeCompare(b.name)));
      setValue('machine', created.name, { shouldValidate: true });
      setValue('machineTypeId', created.id);
      setIsMachineModalOpen(false);
      setMachineDraftName('');
      showToast(t('workOrders.form.toast.machineCreated'), 'success');
    } catch (error) {
      showToast((error as Error).message || t('workOrders.form.toast.machineCreateFailed'), 'error');
    }
  };

  const handleSaveRoaster = async (data: RoasterFormData) => {
    try {
      const created = await roasterService.create(data);
      setRoasters((prev) => [...prev, created].sort((a, b) => a.name.localeCompare(b.name)));
      setValue('roaster', created.name, { shouldValidate: true });
      setValue('roasterId', created.id);
      setIsRoasterModalOpen(false);
      setRoasterDraftName('');
      showToast(t('workOrders.form.toast.roasterCreated'), 'success');
    } catch (error) {
      showToast((error as Error).message || t('workOrders.form.toast.roasterCreateFailed'), 'error');
    }
  };

  if (isLoading) return <LoadingSpinner text={t('workOrders.form.loading')} />;

  const readOnlyDisabledStyles = isReadOnly
    ? ' [&_input:disabled]:bg-gray-100 [&_input:disabled]:text-gray-500 [&_input:disabled]:border-gray-200 [&_input:disabled]:cursor-not-allowed'
      + ' [&_select:disabled]:bg-gray-100 [&_select:disabled]:text-gray-500 [&_select:disabled]:border-gray-200 [&_select:disabled]:cursor-not-allowed'
      + ' [&_textarea:disabled]:bg-gray-100 [&_textarea:disabled]:text-gray-500 [&_textarea:disabled]:border-gray-200 [&_textarea:disabled]:cursor-not-allowed'
      + ' [&_button:disabled:not([data-keep-active])]:opacity-60 [&_button:disabled:not([data-keep-active])]:cursor-not-allowed'
    : '';

  return (
    <>
      <form
        onSubmit={
          shouldPreventSubmit
            ? (e) => e.preventDefault()
            : (e) => {
                // Defense in depth: keep stopwatch-mid-run submissions out even
                // if a button slipped past `disabled` (e.g. Enter-key submit on
                // an input). Draft saves stay allowed.
                if (isStopwatchInProgress && submitActionRef.current !== 'draft') {
                  e.preventDefault();
                  return;
                }
                handleSubmit(onFormSubmit)(e);
              }
        }
        className={`work-order-form space-y-8 p-4 bg-white rounded-lg shadow-sm${readOnlyDisabledStyles}`}
        data-test-id="create-work-order-form"
      >
        {canAutosave && !isReadOnly && (
          <div className="flex justify-end text-xs text-gray-500">{saving ? t('forms.actions.saving') : saved ? t('workOrders.form.saved') : ''}</div>
        )}

        <BasicInfoSection
          register={register}
          control={control}
          setValue={setValue}
          errors={errors}
          initialData={initialData}
          machineOptions={machineOptions}
          roasterOptions={roasterOptions}
          onCreateMachine={handleCreateMachine}
          onCreateRoaster={handleCreateRoaster}
          canCreateMachine={canCreateMachine}
          canCreateRoaster={canCreateRoaster}
          selectedMachine={selectedMachine}
          isCompleted={isReadOnly}
          isAtMachineTypeLimit={isAtMachineTypeLimit}
        />

        <BeanSelectionSection
          isEditMode={isEditMode}
          greenBeans={greenBeans}
          selectedBeanId={selectedBeanId}
          handleBeanChange={handleBeanChange}
          handleAutoCalculateLots={handleAutoCalculateLots}
          isAutoCalculatingLots={isAutoCalculatingLots}
          manualInputWeight={manualInputWeight}
          manualOutputWeight={manualOutputWeight}
          handleWeightInputChange={handleWeightInputChange}
          handleWeightOutputChange={handleWeightOutputChange}
          unit={unit}
          setUnit={setUnit}
          lotOptions={lotOptions}
          lots={lots}
          handleLotChange={handleLotChange}
          handleWeightChange={handleWeightChange}
          addLotRow={addLotRow}
          removeLotRow={removeLotRow}
          totalUsedWeight={totalUsedWeight}
          parsedRoastInputWeight={parsedRoastInputWeight}
          isWeightMismatch={isWeightMismatch}
          useRoastProfile={useRoastProfile}
          roastType={roastType}
          setRoastType={setRoastType}
          isOutputExceedsInput={isOutputExceedsInput}
          isCompleted={isReadOnly}
          allowRoastTypeEdit={allowRoastTypeEdit}
        />

        <RoastProfileSectionWrapper
          isEditMode={isEditMode}
          useRoastProfile={useRoastProfile}
          setUseRoastProfile={setUseRoastProfile}
          useSnapshot={useSnapshot}
          setUseSnapshot={setUseSnapshot}
          useStopwatchMode={useStopwatchMode}
          setUseStopwatchMode={setUseStopwatchMode}
          selectedBeanId={selectedBeanId}
          selectedRecipeId={selectedRecipeId}
          setSelectedRecipeId={setSelectedRecipeId}
          artisanProfiles={artisanProfiles}
          selectedArtisanProfileId={selectedArtisanProfileId}
          linkedArtisanProfileId={linkedArtisanProfileId}
          linkedArtisanProfileTitle={linkedArtisanProfileTitle}
          handleArtisanProfileSelect={handleArtisanProfileSelect}
          onRequestUnlinkArtisanProfile={() => setIsUnlinkModalOpen(true)}
          disableUnlinkArtisanProfile={!canUnlinkArtisanProfile || isUnlinkingArtisanProfile}
          useArtisanProfile={useArtisanProfile}
          setUseArtisanProfile={setUseArtisanProfile}
          recipes={recipes}
          greenBeans={greenBeans}
          roastData={roastData}
          handleProfileChange={handleProfileChange}
          handleStageChange={handleStageChange}
          handleWeightInputChange={handleWeightInputChange}
          handleWeightOutputChange={handleWeightOutputChange}
          handleAgtronChange={handleAgtronChange}
          roastType={roastType}
          handleRoastTypeChange={handleRoastTypeChange}
          saveAsNewRecipe={saveAsNewRecipe}
          setSaveAsNewRecipe={setSaveAsNewRecipe}
          stage={stage}
          seconds={seconds}
          handleStopwatchMainClick={handleStopwatchMainClick}
          handleStopwatchStart={handleStopwatchStart}
          handleStopwatchPause={handleStopwatchPause}
          handleStopwatchRestart={handleStopwatchRestart}
          stopwatchRef={stopwatchRef}
          unit={unit}
          setUnit={setUnit}
          lots={lots}
          isCompleted={isReadOnly}
          isSavedAsRecipe={initialIsSavedAsRecipe}
          allowSaveAsNewRecipeWhenCompleted={allowSaveRecipe}
          allowRoastTypeEdit={allowRoastTypeEdit}
        />

        <WorkOrderActionsBar
          onCancel={onCancel}
          isEditMode={isEditMode}
          initialStatus={initialStatus}
          isSubmitting={isSubmitting}
          isOutputExceedsInput={isOutputExceedsInput}
          canSaveAsComplete={canSaveAsComplete}
          submitActionRef={submitActionRef}
          isCompleted={isReadOnly}
          allowSaveRecipe={allowSaveRecipe}
          saveAsNewRecipe={saveAsNewRecipe}
          canDownload={canDownload}
          downloadData={downloadData}
          isRoastTypeDirty={isRoastTypeDirty}
          onSaveRoastType={() => {
            setRoastTypeSaveError(null);
            setIsRoastTypeWarningOpen(true);
          }}
          isSavingRoastType={isSavingRoastType}
          isStopwatchInProgress={isStopwatchInProgress}
          onboardingActive={onboardingActive}
          onboardingRoastStep={onboardingRoastStep}
        />
      </form>

      <WorkOrderModals
        isMismatchModalOpen={isMismatchModalOpen}
        setIsMismatchModalOpen={setIsMismatchModalOpen}
        pendingSubmitData={pendingSubmitData}
        setPendingSubmitData={setPendingSubmitData}
        isOutputGreaterModalOpen={isOutputGreaterModalOpen}
        setIsOutputGreaterModalOpen={setIsOutputGreaterModalOpen}
        warningMessage={warningMessage}
        setWarningMessage={setWarningMessage}
      />

      <RoastTypeWarningModal
        isOpen={isRoastTypeWarningOpen}
        onClose={() => {
          setIsRoastTypeWarningOpen(false);
          setRoastTypeSaveError(null);
        }}
        onConfirm={async () => {
          if (!initialData || !('id' in initialData) || !initialData.id) return;
          const id = initialData.id as string;
          setIsSavingRoastType(true);
          setRoastTypeSaveError(null);
          try {
            await workOrderService.updateRoastType(id, roastType);
            commitRoastTypeAsInitial(roastType);
            queryClient.invalidateQueries({ queryKey: ['work-orders'] });
            queryClient.invalidateQueries({ queryKey: ['work-order', id] });
            setIsRoastTypeWarningOpen(false);
            showToast(t('workOrders.roastTypeEdit.savedToast'), 'success');
          } catch (error) {
            const errResp = error as { response?: { data?: { error?: string; message?: string } } };
            const message =
              errResp?.response?.data?.error ||
              errResp?.response?.data?.message ||
              (error as Error).message ||
              t('workOrders.roastTypeEdit.saveFailed');
            setRoastTypeSaveError(message);
          } finally {
            setIsSavingRoastType(false);
          }
        }}
        isSaving={isSavingRoastType}
        error={roastTypeSaveError}
        initialRoastType={initialRoastType}
        roastType={roastType}
        roastTypeLabelMap={roastTypeLabelMap}
      />

      <CompleteWorkOrderSummaryModal
        isOpen={isCompleteSummaryModalOpen}
        onClose={cancelCompleteSummary}
        onConfirm={confirmCompleteSubmit}
        submitting={isSubmitting}
        beanName={completeSummaryData.beanName}
        lots={completeSummaryData.lots}
        inputWeight={completeSummaryData.inputWeight}
        outputWeight={completeSummaryData.outputWeight}
        agtronWhole={completeSummaryData.agtronWhole}
        agtronGround={completeSummaryData.agtronGround}
        agtronScaa={completeSummaryData.agtronScaa}
        agtronCommon={completeSummaryData.agtronCommon}
        roastTypeLabel={completeSummaryData.roastTypeLabel}
      />

      <UnlinkArtisanProfileModal
        isOpen={isUnlinkModalOpen}
        onClose={() => setIsUnlinkModalOpen(false)}
        onConfirm={async () => {
          if (!initialData || !('id' in initialData) || !initialData.id) return;
          setIsUnlinkingArtisanProfile(true);
          try {
            const updated = await workOrderService.unlinkArtisanProfile(initialData.id);
            handleArtisanProfileUnlinked(updated);
            // The Artisan Sync list is cached fresh for five minutes, so without this it
            // keeps showing this profile as synced — and its delete control disabled —
            // after the user switches back to that tab (AS-005).
            invalidateArtisanLinkQueries(queryClient);
            showToast(t('workOrders.form.toast.artisanUnlinked'), 'success');
            setIsUnlinkModalOpen(false);
          } catch (error) {
            showToast((error as Error).message || t('workOrders.form.toast.artisanUnlinkFailed'), 'error');
          } finally {
            setIsUnlinkingArtisanProfile(false);
          }
        }}
        isUnlinking={isUnlinkingArtisanProfile}
        canUnlink={canUnlinkArtisanProfile}
      />

      <Modal
        isOpen={isMachineModalOpen}
        onClose={() => setIsMachineModalOpen(false)}
        title={t('workOrders.form.addMachineType')}
      >
        <MachineForm
          initialData={{ name: machineDraftName, isActive: true }}
          forceMode="type"
          onSubmit={handleSaveMachine}
          onCancel={() => setIsMachineModalOpen(false)}
          onTypeCreated={(item) => {
            setMachines((prev) => {
              const parentMachine = prev.find((machine) => machine.id === item.machineId);
              return [
                ...prev,
                {
                  id: item.id,
                  name: item.name,
                  isActive: item.isActive,
                  isSampelRoaster: parentMachine?.isSampelRoaster ?? false,
                  minimumInputKg: item.minimumInputKg,
                  maximumInputKg: item.maximumInputKg,
                  parentMachineId: item.machineId,
                },
              ];
            });
            setValue('machine', item.name, { shouldValidate: true });
            setValue('machineTypeId', item.id);
            setIsMachineModalOpen(false);
            setMachineDraftName('');
            showToast(t('workOrders.form.toast.machineTypeCreated'), 'success');
          }}
        />
      </Modal>

      <Modal
        isOpen={isRoasterModalOpen}
        onClose={() => setIsRoasterModalOpen(false)}
        title={t('workOrders.form.addRoaster')}
      >
        <RoasterForm
          initialData={{ name: roasterDraftName, isActive: true }}
          onSubmit={handleSaveRoaster}
          onCancel={() => setIsRoasterModalOpen(false)}
        />
      </Modal>
    </>
  );
};
